import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useMultiGameBadgesQuery,
  useMultiRegionLolQuery,
} from '@/services/qriotsApi';
import type { GameCode, Platform, RegionPresence } from '@/types/qriots';
import { classifyQriotsError } from '@/services/qriotsErrors';
import { Loader } from '@/components/common/Loader';
import { RiotSearchForm } from '@/components/riot/RiotSearchForm';
import { MultiGameBadgesCard } from '@/components/riot/MultiGameBadgesCard';
import { QriotsErrorState } from '@/components/riot/QriotsErrorState';
import { RegionPickerModal } from '@/components/riot/RegionPickerModal';

function pickPrimaryRegion(presences: RegionPresence[]): Platform | null {
  if (presences.length === 0) return null;
  return [...presences].sort((a, b) =>
    (b.summonerLevel - a.summonerLevel) ||
    (b.revisionDate - a.revisionDate) ||
    a.platform.localeCompare(b.platform),
  )[0]!.platform;
}

export function Riot() {
  const navigate = useNavigate();
  const { t } = useTranslation('riot');

  const [submitted, setSubmitted] = useState<{ gameName: string; tagLine: string } | null>(null);
  const [region, setRegion] = useState<Platform | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerHint, setPickerHint] = useState<string | undefined>(undefined);

  // Step 1: auto-detect region from multi-region (only after user submits)
  const multiRegion = useMultiRegionLolQuery(submitted ?? { gameName: '', tagLine: '' }, {
    skip: !submitted,
  });

  // When multi-region resolves and we haven't picked a region yet, derive
  // the primary or open the picker (effect, not render — avoids React
  // setState-during-render warnings).
  useEffect(() => {
    if (!submitted || region || !multiRegion.data) return;
    const primary = pickPrimaryRegion(multiRegion.data.presences);
    if (primary) {
      setRegion(primary);
    } else {
      setPickerHint(t('error.noLolAccount'));
      setPickerOpen(true);
    }
  }, [submitted, region, multiRegion.data, t]);

  // Step 2: fetch multi-game-badges once we have region
  const badges = useMultiGameBadgesQuery(
    submitted && region ? { ...submitted, region } : { gameName: '', tagLine: '', region: 'EUW1' as Platform },
    { skip: !submitted || !region },
  );

  const onSubmit = (rid: { gameName: string; tagLine: string }) => {
    setSubmitted(rid);
    setRegion(null);
  };

  const onEditId = () => {
    setSubmitted(null);
    setRegion(null);
    setPickerOpen(false);
  };

  const onPickGame = (game: GameCode) => {
    if (game !== 'lol' || !badges.data || !region) return;
    navigate(
      `/riot/lol/${encodeURIComponent(badges.data.puuid)}?region=${region}&gameName=${encodeURIComponent(badges.data.gameName)}&tagLine=${encodeURIComponent(badges.data.tagLine)}`,
    );
  };

  // Render: empty / search form
  if (!submitted) {
    return <RiotSearchForm onSubmit={onSubmit} />;
  }

  // Render: errors from either query
  const err = multiRegion.error ?? badges.error;
  if (err) {
    const { kind, retryAfterSec } = classifyQriotsError(err);
    return <QriotsErrorState kind={kind} retryAfterSec={retryAfterSec} onRetry={() => onSubmit(submitted)} onEditId={onEditId} />;
  }

  // Render: loading (either query in flight)
  if (multiRegion.isLoading || (region && badges.isLoading)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Render: result
  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      {badges.data && region && (
        <MultiGameBadgesCard
          data={badges.data}
          freshness={{ fetchedAt: new Date().toISOString(), ttlSeconds: 600 }}
          region={region}
          onSwitchRegion={() => setPickerOpen(true)}
          onPickGame={onPickGame}
          onEditId={onEditId}
        />
      )}
      {pickerOpen && (
        <RegionPickerModal
          current={region ?? undefined}
          hint={pickerHint}
          onPick={(p) => {
            setRegion(p);
            setPickerOpen(false);
            setPickerHint(undefined);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
